/**
 * Live Event Map — real-time event locations for a single app, rendered as a
 * GeoJSON circle layer (not per-point DOM markers) so it stays fast even
 * under a heavy event stream. Points decay and are pruned once they've been
 * quiet for a while, so the map reflects "what's happening now".
 */
import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { liveEventsService, type LiveEvent } from '../services/liveEventsService'
import { useAuthStore } from '../stores/authStore'
import { isMapboxTokenConfigured, toCoordinate } from '../utils/mapbox'

interface LiveEventMapProps {
  appId: string
}

interface LivePoint {
  lat: number
  lng: number
  city: string | null
  country: string | null
  count: number
  lastSeen: number
}

/** Points stop refreshing their glow and are dropped once this quiet. */
const POINT_TTL_MS = 5 * 60 * 1000
/** How often the map re-renders from the point buffer (decay + new arrivals). */
const TICK_MS = 2000

export default function LiveEventMap({ appId }: LiveEventMapProps) {
  const { user } = useAuthStore()
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const popupRef = useRef<mapboxgl.Popup | null>(null)
  const pointsRef = useRef<Map<string, LivePoint>>(new Map())
  const loadedRef = useRef(false)
  const [mapReady, setMapReady] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [hasEverConnected, setHasEverConnected] = useState(false)
  const [pointCount, setPointCount] = useState(0)

  const mapboxToken = (import.meta.env.VITE_MAPBOX_TOKEN || '').trim()
  const hasMapboxToken = isMapboxTokenConfigured(mapboxToken)
  if (hasMapboxToken) {
    mapboxgl.accessToken = mapboxToken
  }

  // ---------------------------------------------------------------------
  // Initialize map + layers once
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!hasMapboxToken || !containerRef.current || mapRef.current) return

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-20, 25],
      zoom: 1.3,
      projection: { name: 'mercator' },
      attributionControl: false,
    })
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')

    map.on('load', () => {
      loadedRef.current = true
      setMapReady(true)
      map.resize()

      map.addSource('live-events', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })

      // Soft glow behind each point, fading out as the point goes stale.
      map.addLayer({
        id: 'live-events-glow',
        type: 'circle',
        source: 'live-events',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 16, 20, 30],
          'circle-color': '#818cf8',
          'circle-blur': 1,
          'circle-opacity': ['interpolate', ['linear'], ['get', 'freshness'], 0, 0, 1, 0.28],
        },
      })

      // The point itself — brighter and more opaque the more recently it fired.
      map.addLayer({
        id: 'live-events-point',
        type: 'circle',
        source: 'live-events',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 1, 4, 20, 11],
          'circle-color': '#c4b5fd',
          'circle-stroke-color': 'rgba(255,255,255,0.9)',
          'circle-stroke-width': 1.5,
          'circle-opacity': ['interpolate', ['linear'], ['get', 'freshness'], 0, 0.25, 1, 0.95],
        },
      })

      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: '260px',
      })

      map.on('mousemove', 'live-events-point', (event) => {
        const feature = event.features?.[0]
        if (!feature || feature.geometry.type !== 'Point') return
        map.getCanvas().style.cursor = 'pointer'
        popupRef.current
          ?.setLngLat(event.lngLat)
          .setHTML(renderLivePopup(feature.properties ?? {}))
          .addTo(map)
      })
      map.on('mouseleave', 'live-events-point', () => {
        map.getCanvas().style.cursor = ''
        popupRef.current?.remove()
      })
    })

    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      popupRef.current = null
      loadedRef.current = false
      setMapReady(false)
    }
  }, [hasMapboxToken])

  // ---------------------------------------------------------------------
  // Subscribe to this app's live event stream
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!user) return
    setHasEverConnected(false)
    pointsRef.current.clear()

    if (liveEventsService.getIsConnected()) {
      setIsConnected(true)
      setHasEverConnected(true)
    }

    liveEventsService.connect(user.id, user.id, appId).catch((error) => {
      console.error('[LiveEventMap] Failed to connect to live events:', error)
    })

    const unsubscribeEvent = liveEventsService.onEvent((event: LiveEvent) => {
      if (event.appId !== appId) return
      const lat = toCoordinate(event.latitude, -90, 90)
      const lng = toCoordinate(event.longitude, -180, 180)
      if (lat === null || lng === null) return

      // Round to ~1km so nearby events light up the same dot instead of overplotting.
      const key = `${lat.toFixed(2)},${lng.toFixed(2)}`
      const existing = pointsRef.current.get(key)
      pointsRef.current.set(key, {
        lat,
        lng,
        city: event.city ?? existing?.city ?? null,
        country: event.country ?? existing?.country ?? null,
        count: (existing?.count ?? 0) + 1,
        lastSeen: Date.now(),
      })
    })

    const unsubscribeConnected = liveEventsService.onConnected(() => {
      setIsConnected(true)
      setHasEverConnected(true)
    })
    const unsubscribeDisconnected = liveEventsService.onDisconnected(() => setIsConnected(false))

    return () => {
      unsubscribeEvent()
      unsubscribeConnected()
      unsubscribeDisconnected()
    }
  }, [user, appId])

  // ---------------------------------------------------------------------
  // Repaint tick: decay freshness, prune stale points, push GeoJSON
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current
    if (!map) return

    const tick = () => {
      const now = Date.now()
      const points = pointsRef.current
      for (const [key, point] of points) {
        if (now - point.lastSeen > POINT_TTL_MS) points.delete(key)
      }

      const source = map.getSource('live-events') as mapboxgl.GeoJSONSource | undefined
      if (!source) return
      source.setData({
        type: 'FeatureCollection',
        features: Array.from(points.values()).map((point) => ({
          type: 'Feature',
          properties: {
            count: point.count,
            city: point.city,
            country: point.country,
            freshness: Math.max(0, 1 - (now - point.lastSeen) / POINT_TTL_MS),
          },
          geometry: { type: 'Point', coordinates: [point.lng, point.lat] },
        })),
      })
      setPointCount(points.size)
    }

    tick()
    const id = window.setInterval(tick, TICK_MS)
    return () => window.clearInterval(id)
  }, [mapReady])

  return (
    <div className="relative h-full w-full overflow-hidden rounded-2xl">
      <div ref={containerRef} className="absolute inset-0" />

      {!hasMapboxToken && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
          <p className="px-4 text-center text-sm text-gray-300">
            Map is unavailable. Set `VITE_MAPBOX_TOKEN` to enable map tiles.
          </p>
        </div>
      )}

      {hasMapboxToken && (
        <div className="absolute left-4 top-4 z-10 glass-pill px-3 py-1.5 text-xs font-medium">
          <span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full ${isConnected ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
          {isConnected ? `Live · ${pointCount} active ${pointCount === 1 ? 'location' : 'locations'}` : 'Reconnecting…'}
        </div>
      )}

      {hasMapboxToken && !isConnected && !hasEverConnected && (
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
          <p className="glass-pill inline-block px-4 py-2 text-sm text-gray-400">Connecting to live events...</p>
        </div>
      )}

      {hasMapboxToken && isConnected && pointCount === 0 && (
        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <p className="glass-pill inline-block px-4 py-2 text-sm text-gray-500">Waiting for live events…</p>
        </div>
      )}
    </div>
  )
}

function renderLivePopup(properties: Record<string, unknown>) {
  const count = Number(properties.count ?? 0)
  const city = typeof properties.city === 'string' ? properties.city : ''
  const country = typeof properties.country === 'string' ? properties.country : ''
  const place = [city, country].filter(Boolean).join(', ') || 'Unknown location'

  return `
    <div style="min-width:180px;border:1px solid rgba(255,255,255,0.14);border-radius:16px;background:rgba(3,7,18,0.94);padding:12px 14px;box-shadow:0 24px 80px rgba(0,0,0,0.5);backdrop-filter:blur(16px);">
      <div style="font-size:10px;font-weight:800;letter-spacing:0.14em;text-transform:uppercase;color:#a78bfa;">Live</div>
      <div style="margin-top:4px;font-size:14px;font-weight:800;color:white;">${escapeHtml(place)}</div>
      <div style="margin-top:2px;font-size:12px;color:rgba(209,213,219,0.72);">${count.toLocaleString()} ${count === 1 ? 'event' : 'events'} recently</div>
    </div>
  `
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}
